import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, type OnDestroy, Output } from '@angular/core';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { SubSink } from 'subsink';
import type { RemoteUserLocation } from '../../../shared/models/api/remote-user-location.model';
import { RemoteUserLocationService } from '../../../shared/services/api/remote-user-location.service';

@Component({
  selector: 'opa-remote-user-location-delete',
  imports: [CommonModule, PorscheDesignSystemModule],
  templateUrl: './remote-user-location-delete.component.html',
  styleUrl: './remote-user-location-delete.component.scss',
})
export class RemoteUserLocationDeleteComponent implements OnDestroy {
  private readonly remoteUserLocationService = inject(RemoteUserLocationService);
  private readonly toastManager = inject(ToastManager);

  @Input({ required: true }) open = false;
  @Input({ required: true }) remoteUserLocation: RemoteUserLocation | undefined = undefined;
  @Output() dismiss = new EventEmitter<RemoteUserLocation | undefined>();

  private readonly subSink = new SubSink();
  private readonly submitting = new BehaviorSubject(false);
  readonly submitting$ = this.submitting.pipe(distinctUntilChanged());

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(remoteUserLocation?: RemoteUserLocation): void {
    this.dismiss.emit(remoteUserLocation);
  }

  submit(): void {
    const remoteUserLocation = this.remoteUserLocation;
    if (!remoteUserLocation) return;

    this.submitting.next(true);
    this.subSink.sink = this.remoteUserLocationService
      .deleteRemoteUserLocation(remoteUserLocation.id)
      .pipe(
        tap({
          next: () => {
            this.toastManager.addMessage({
              text: `Remote User Location ${remoteUserLocation.description} deleted`,
              state: 'success',
            });
            this.submitting.next(false);
            this.dismiss.emit(remoteUserLocation);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }
}
